import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/shared";
import { useToast } from "@/hooks/use-toast";
import { EvaOsMark } from "@/components/landing/EvaOsMark";

const cream = "#F8F4EC";
const ink = "#1a1a1a";
const muted = "#6b6357";
const gold = "#a08c6e";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [ready, setReady] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    // Supabase handles the recovery token from the URL hash automatically via onAuthStateChange.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") setReady(true);
    });
    // Fallback: if the user is already in a session (link click landed cleanly), allow update.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) {
      toast({ title: "Couldn't update password", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Password updated", description: "You're signed in." });
    navigate("/ambassadors/dashboard", { replace: true });
  };

  return (
    <div style={{ minHeight: "100vh", background: cream, color: ink, fontFamily: "Barlow, sans-serif", lineHeight: 1.6 }}>
      <header style={{ borderBottom: "1px solid rgba(0,0,0,0.08)", padding: "20px 24px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Link to="/" style={{ textDecoration: "none", color: "inherit" }}><EvaOsMark /></Link>
          <Link to="/auth" style={{ fontSize: 13, color: muted, textDecoration: "none" }}>← Back to sign-in</Link>
        </div>
      </header>
      <main style={{ maxWidth: 460, margin: "0 auto", padding: "80px 24px 120px" }}>
        <p style={{ fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", color: gold, marginBottom: 12 }}>
          Reset password
        </p>
        <h1 style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: "clamp(40px, 6vw, 64px)", lineHeight: 1, margin: "0 0 16px", fontWeight: 400 }}>
          Set a new password.
        </h1>
        {!ready ? (
          <p style={{ color: muted }}>Verifying your reset link…</p>
        ) : (
          <form onSubmit={onSubmit} style={{ display: "grid", gap: 12, marginTop: 24 }}>
            <input type="password" placeholder="New password (min 8 chars)" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} autoComplete="new-password" style={inputStyle} />
            <input type="password" placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8} autoComplete="new-password" style={inputStyle} />
            <button type="submit" disabled={busy} style={{ padding: "13px 16px", border: "none", borderRadius: 10, background: ink, color: cream, fontSize: 15, fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1, marginTop: 4 }}>
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
      </main>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding: "12px 14px",
  border: "1px solid rgba(0,0,0,0.14)",
  borderRadius: 10,
  background: "#fff",
  fontSize: 15,
  fontFamily: "inherit",
  color: "#1a1a1a",
  outline: "none",
};